import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import './App.css';
import { DisclaimerModal } from './components/Disclaimer.js'
import { RollAnimationPopup } from './components/RollAnimationPopup.js';
import { ResultPopup } from './components/ResultPopup.js';
import {CONTRACT_ABI, DERP_ABI} from './constants.js'
// Contract addresses (update these with your deployed addresses)
const CONTRACT_ADDRESS = "0x4B2EB0626c563866bEF17C1a96F492D4f74f6e71"; // Your deployed contract address
const DERP_TOKEN_ADDRESS = "0xe920d1da9a4d59126dc35996ea242d60efca1304"; // DERP token address

const SONIC_MAINNET = {
  chainId: "0x92", // 146 in hex
  chainName: "Sonic Mainnet",
  nativeCurrency: {
    name: "S",
    symbol: "S",
    decimals: 18
  },
  rpcUrls: ["https://rpc.soniclabs.com"],
  blockExplorerUrls: ["https://sonicscan.org/"]
};

export function App() {
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [account, setAccount] = useState("");
  const [network, setNetwork] = useState(null);
  const [contract, setContract] = useState(null);
  const [token, setToken] = useState(null);
  
  // Disclaimer state
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [hasAcceptedDisclaimer, setHasAcceptedDisclaimer] = useState(() => {
    // Check localStorage for previous acceptance
    return localStorage.getItem('derpquest_disclaimer_accepted') === 'true';
  });
  
  // Game state
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [wager, setWager] = useState("");
  const [requestPrice, setRequestPrice] = useState("0");
  
  // User balances
  const [userBalance, setUserBalance] = useState("0");
  const [userDeposit, setUserDeposit] = useState("0");
  const [userShare, setUserShare] = useState("0");
  const [userWithdrawable, setUserWithdrawable] = useState("0");
  const [houseBalance, setHouseBalance] = useState("0");
  const [maxWager, setMaxWager] = useState("0");
  
  // Deposit state
  const [depositAmount, setDepositAmount] = useState("");
  
  // Request history
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ total: 0, fulfilled: 0, pending: 0 });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isPaused, setIsPaused] = useState(false);
  
  // Popup state
  const [showRollAnimation, setShowRollAnimation] = useState(false);
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [rollResult, setRollResult] = useState(null);

  const [previousRoll, setPreviousRoll] = useState({ choice: null, wager: "" });
  const [isApprovingLarge, setIsApprovingLarge] = useState(false);
    // House admin address check
    const HOUSE_ADMIN_ADDRESS = "0x8DE3c3891268502F77DB7E876d727257DEc0F852";
    const [isHouseAdmin, setIsHouseAdmin] = useState(false);

    useEffect(() => {
      if (account && account.toLowerCase() === HOUSE_ADMIN_ADDRESS.toLowerCase()) {
        setIsHouseAdmin(true);
      } else {
        setIsHouseAdmin(false);
      }
    }, [account]);
  
  
  // Track pending requests and processed events
  const processedEvents = useRef(new Set());
  const isPolling = useRef(false);
  const currentRollParams = useRef({ choice: null, wager: null });

  // Initialize provider
  useEffect(() => {
    if (window.ethereum && hasAcceptedDisclaimer) {
      const prov = new ethers.BrowserProvider(window.ethereum);
      setProvider(prov);
      
      // Listen for account changes
      window.ethereum.on('accountsChanged', async (accounts) => {
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          const signer = await prov.getSigner();
          initializeContracts(signer);
        } else {
          setAccount("");
          setSigner(null);
          setContract(null);
          setToken(null);
        }
      });
      
      // Listen for network changes - DO NOT reload page, just update state
      window.ethereum.on('chainChanged', async (chainId) => {
        console.log("Network changed to:", chainId);
        
        // Get new provider instance with updated network
        const newProv = new ethers.BrowserProvider(window.ethereum);
        setProvider(newProv);
        
        // Update network state
        const newNetwork = await newProv.getNetwork();
        setNetwork(newNetwork);
        
        // If we have an account, reinitialize contracts with new network
        if (account) {
          try {
            const signer = await newProv.getSigner();
            await initializeContracts(signer);
            
            if (Number(chainId) === 146) {
              setStatus({ type: "success", message: "Connected to Sonic Mainnet!" });
            } else {
              setStatus({ 
                type: "warning", 
                message: "⚠️ Wrong Network! Please switch to Sonic Mainnet (Chain ID: 146)" 
              });
            }
          } catch (error) {
            console.error("Error reinitializing after network change:", error);
          }
        }
      });
      
      // Cleanup event listeners on unmount
      return () => {
        if (window.ethereum) {
          window.ethereum.removeAllListeners('accountsChanged');
          window.ethereum.removeAllListeners('chainChanged');
        }
      };
    }
  }, [hasAcceptedDisclaimer, account]);

  // Initialize contracts when signer changes
  const initializeContracts = async (signer) => {
    try {
      const diceContract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signer);
      const tokenContract = new ethers.Contract(DERP_TOKEN_ADDRESS, DERP_ABI, signer);
      
      setContract(diceContract);
      setToken(tokenContract);
      setSigner(signer);
      
      // Load initial data
      await loadContractData(diceContract, tokenContract);
      await loadUserData(signer, diceContract, tokenContract);
    } catch (error) {
      console.error("Error initializing contracts:", error);
      setStatus({ type: "error", message: "Failed to initialize contracts" });
    }
  };

  // Load contract data
  const loadContractData = async (diceContract, tokenContract) => {
    try {
      const [price, houseBal, maxWag, pausedStatus] = await Promise.all([
        diceContract.getRequestPrice(),
        diceContract.getHouseBalance(),
        diceContract.getMaxAllowedWager(),
        diceContract.paused()
      ]);
      
      setRequestPrice(ethers.formatEther(price));
      setHouseBalance(ethers.formatEther(houseBal));
      setMaxWager(ethers.formatEther(maxWag));
      setIsPaused(pausedStatus);
      
      // Load stats
      const statsData = await diceContract.getStats();
      setStats({
        total: Number(statsData[0]),
        fulfilled: Number(statsData[1]),
        pending: Number(statsData[2])
      });
      
      console.log("Contract data loaded, total rolls:", Number(statsData[0]));
      
    } catch (error) {
      console.error("Error loading contract data:", error);
    }
  };

  // Load user data
  const loadUserData = async (signer, diceContract, tokenContract) => {
    try {
      const address = await signer.getAddress();
      
      const [balance, depositInfo] = await Promise.all([
        tokenContract.balanceOf(address),
        diceContract.getUserDepositInfo(address)
      ]);
      
      setUserBalance(ethers.formatEther(balance));
      
      if (depositInfo[0] > 0n) {
        setUserDeposit(ethers.formatEther(depositInfo[0]));
        setUserShare(ethers.formatEther(depositInfo[1]));
        setUserWithdrawable(ethers.formatEther(depositInfo[4]));
      } else {
        setUserDeposit("0");
        setUserShare("0");
        setUserWithdrawable("0");
      }
    } catch (error) {
      console.error("Error loading user data:", error);
    }
  };

  // Handle disclaimer acceptance
  const handleAcceptDisclaimer = () => {
    localStorage.setItem('derpquest_disclaimer_accepted', 'true');
    setHasAcceptedDisclaimer(true);
    setShowDisclaimer(false);
  };

 // Connect wallet with automatic network switching
const connectWallet = async () => {
  if (!window.ethereum) {
    setStatus({ type: "error", message: "Please install MetaMask!" });
    return;
  }
  
  if (!hasAcceptedDisclaimer) {
    setShowDisclaimer(true);
    return;
  }
  
  try {
    setLoading(true);
    setStatus({ type: "pending", message: "Connecting..." });
    
    // First, try to switch to Sonic Mainnet before requesting accounts
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: SONIC_MAINNET.chainId }]
      });
    } catch (switchError) {
      // If network doesn't exist in wallet, add it
      if (switchError.code === 4902) {
        setStatus({ type: "pending", message: "Adding Sonic Mainnet to your wallet..." });
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [SONIC_MAINNET]
        });
      } else if (switchError.code === 4001) {
        // User rejected the network switch
        setStatus({ type: "error", message: "Please connect to Sonic Mainnet to continue" });
        setLoading(false);
        return;
      } else {
        throw switchError;
      }
    }
    
    // Now request accounts after successful network switch
    const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
    
    if (accounts.length === 0) {
      throw new Error("No accounts found. Please connect your wallet.");
    }
    
    // Initialize provider and signer
    const prov = new ethers.BrowserProvider(window.ethereum);
    const signer = await prov.getSigner();
    const address = accounts[0];
    
    // Double-check we're on the correct network
    const network = await prov.getNetwork();
    if (Number(network.chainId) !== 146) {
      throw new Error("Failed to switch to Sonic Mainnet. Please try again.");
    }
    
    setAccount(address);
    await initializeContracts(signer);
    setStatus({ type: "success", message: "Connected to Sonic Mainnet!" });
    
  } catch (error) {
    console.error("Error connecting wallet:", error);
    
    let errorMessage = "Connection failed";
    if (error.code === 4001) {
      errorMessage = "Connection rejected by user";
    } else if (error.message.includes("user rejected")) {
      errorMessage = "Network switch was rejected";
    } else if (error.message.includes("Sonic Mainnet")) {
      errorMessage = error.message;
    } else {
      errorMessage = `Connection error: ${error.message}`;
    }
    
    setStatus({ 
      type: "error", 
      message: errorMessage
    });
  } finally {
    setLoading(false);
  }
};
// Also update the useEffect that checks network on initial load to be more proactive
useEffect(() => {
  const checkAndSwitchNetwork = async () => {
    if (window.ethereum && provider && hasAcceptedDisclaimer && !account) {
      try {
        const network = await provider.getNetwork();
        setNetwork(network);
        
        // If not on Sonic Mainnet and no account is connected yet,
        // show a more prominent message
        if (Number(network.chainId) !== 146) {
          console.log("Not on Sonic Mainnet, showing network prompt");
          // Don't auto-switch here as it might be intrusive
          // Just show the warning
        }
      } catch (error) {
        console.error("Error checking network:", error);
      }
    }
  };
  
  checkAndSwitchNetwork();
}, [provider, account, hasAcceptedDisclaimer]);

const switchToSonicNetwork = async () => {
  if (!window.ethereum) {
    setStatus({ type: "error", message: "Please install MetaMask!" });
    return false;
  }
  
  try {
    setLoading(true);
    setStatus({ type: "pending", message: "Switching to Sonic Mainnet..." });
    
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: SONIC_MAINNET.chainId }]
    });
    
    // Refresh provider after network switch
    const prov = new ethers.BrowserProvider(window.ethereum);
    setProvider(prov);
    const network = await prov.getNetwork();
    setNetwork(network);
    
    if (Number(network.chainId) === 146) {
      setStatus({ type: "success", message: "Switched to Sonic Mainnet!" });
      return true;
    }
    return false;
    
  } catch (switchError) {
    // If network doesn't exist in wallet, add it
    if (switchError.code === 4902) {
      try {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [SONIC_MAINNET]
        });
        setStatus({ type: "success", message: "Sonic Mainnet added and switched!" });
        return true;
      } catch (addError) {
        console.error("Error adding network:", addError);
        setStatus({ 
          type: "error", 
          message: "Failed to add Sonic Mainnet network" 
        });
        return false;
      }
    } else if (switchError.code === 4001) {
      setStatus({ 
        type: "error", 
        message: "Network switch rejected by user" 
      });
      return false;
    } else {
      console.error("Error switching network:", switchError);
      setStatus({ 
        type: "error", 
        message: "Failed to switch network" 
      });
      return false;
    }
  } finally {
    setLoading(false);
  }
};


  // Approve tokens for spending
  const approveTokens = async (amount) => {
    if (!token || !account || (network && Number(network.chainId) !== 146)) return false;
    
    try {
      setLoading(true);
      setStatus({ type: "pending", message: "Approving tokens..." });
      
      const amountWei = ethers.parseEther(amount);
      const tx = await token.approve(CONTRACT_ADDRESS, amountWei);
      await tx.wait();
      
      setStatus({ type: "success", message: "Tokens approved successfully!" });
      return true;
    } catch (error) {
      console.error("Error approving tokens:", error);
      setStatus({ type: "error", message: "Token approval failed: " + error.message });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const approveLargeAmount = async () => {
    if (!checkNetworkBeforeAction()) return;
    
    if (!token || !account) {
      setStatus({ type: "error", message: "Wallet not connected" });
      return;
    }
    
    try {
      setIsApprovingLarge(true);
      setLoading(true);
      setStatus({ type: "pending", message: "Approving large amount (100M DERP)..." });
      
      // Approve 100 million DERP
      const largeAmount = ethers.parseEther("100000000"); // 100M DERP
      const tx = await token.approve(CONTRACT_ADDRESS, largeAmount);
      
      setStatus({ type: "pending", message: "Waiting for confirmation..." });
      await tx.wait();
      
      setStatus({ type: "success", message: "Approved 100M DERP successfully!" });
      
      // Refresh allowance info
      if (contract && account) {
        const allowance = await token.allowance(account, CONTRACT_ADDRESS);
        console.log("New allowance:", ethers.formatEther(allowance), "DERP");
      }
      
    } catch (error) {
      console.error("Error approving large amount:", error);
      setStatus({ type: "error", message: "Approval failed: " + error.message });
    } finally {
      setIsApprovingLarge(false);
      setLoading(false);
    }
  };

  // Check if user is on correct network
  const checkNetworkBeforeAction = () => {
    if (!network || Number(network.chainId) !== 146) {
      setStatus({ 
        type: "error", 
        message: "Please switch to Sonic Mainnet (Chain ID: 146)" 
      });
      return false;
    }
    return true;
  };

  // Roll dice
  const rollDice = async (usePrevious = false) => {
    if (!checkNetworkBeforeAction()) return;
    
    // If using previous roll, use stored parameters
    let rollChoice = selectedNumber;
    let rollWager = wager;
    
    if (usePrevious && previousRoll.choice && previousRoll.wager) {
      rollChoice = previousRoll.choice;
      rollWager = previousRoll.wager;
    }
    
    if (!contract || !rollChoice || !rollWager || parseFloat(rollWager) <= 0) {
      setStatus({ type: "error", message: "Please select a number and enter a valid wager" });
      return;
    }
    
    if (isPaused) {
      setStatus({ type: "error", message: "Contract is currently paused" });
      return;
    }
    
    try {
      setLoading(true);
      setStatus({ type: "pending", message: "Checking allowance..." });
      
      // Check and approve tokens if needed
      const allowance = await token.allowance(account, CONTRACT_ADDRESS);
      const wagerWei = ethers.parseEther(rollWager);
      
      if (allowance < wagerWei) {
        const approved = await approveTokens(rollWager);
        if (!approved) return;
      }
      
      // Get required native token amount
      const requiredNative = await contract.getRequestPrice();
      
      setStatus({ type: "pending", message: "Rolling dice..." });
      
      // Store current roll parameters for matching
      currentRollParams.current = {
        choice: rollChoice,
        wager: rollWager
      };
      
      // Also store in state for rollAgain functionality
      setPreviousRoll({
        choice: rollChoice,
        wager: rollWager
      });
      
      // Show rolling animation
      setShowRollAnimation(true);
      
      // Roll with both wager and native token for gas
      const tx = await contract.roll(wagerWei, rollChoice, {
        value: requiredNative
      });
      
      setStatus({ type: "pending", message: "Waiting for confirmation..." });
      const receipt = await tx.wait();
      
      console.log("Transaction confirmed, starting to poll for result...");
      
      // Only clear inputs if not using previous roll
      if (!usePrevious) {
        setSelectedNumber(null);
        setWager("");
      }
      
      // Start polling for the result
      startPollingForResult();
      
      setStatus({ 
        type: "pending", 
        message: "Dice roll submitted! Waiting for VRF randomness..." 
      });
      
      // Set loading to false (animation will show instead)
      setLoading(false);
      
    } catch (error) {
      console.error("Error rolling dice:", error);
      setStatus({ type: "error", message: "Roll failed: " + error.message });
      setShowRollAnimation(false);
      setLoading(false);
    }
  };
  
  // Start polling for roll result
  const startPollingForResult = async () => {
    if (isPolling.current) {
      console.log("Already polling, skipping...");
      return;
    }
    
    console.log("Starting to poll for result...");
    isPolling.current = true;
    
    // Poll for 3 minutes max
    const maxAttempts = 180; // 180 attempts * 1 second = 3 minutes
    let attempts = 0;
    
    const pollInterval = setInterval(async () => {
      attempts++;
      
      if (attempts > maxAttempts) {
        console.log(`Poll timed out after ${maxAttempts} attempts`);
        clearInterval(pollInterval);
        isPolling.current = false;
        setShowRollAnimation(false);
        setStatus({ 
          type: "warning", 
          message: "Result taking longer than expected. Please check your transaction history." 
        });
        return;
      }
      
      try {
        console.log(`Poll attempt ${attempts}: Checking for new roll...`);
        
        // Get current block number
        const currentBlock = await provider.getBlockNumber();
        const fromBlock = Math.max(currentBlock - 100, 0); // Only check last 100 blocks
        
        // Get events specifically from recent blocks
        const filter = contract.filters.RandomDiceRoll(null, account);
        const events = await contract.queryFilter(filter, fromBlock, currentBlock);
        
        console.log(`Found ${events.length} events in recent blocks`);
        
        // Look for events that match our expected parameters AND are new
        for (const event of events) {
          const [requestId, requester, choice, result, wager, payout, won] = event.args;
          const eventKey = `${requestId.toString()}-${event.transactionHash}`;
          
          // Check if this is our event (matching account) and not already processed
          if (requester.toLowerCase() === account.toLowerCase() && !processedEvents.current.has(eventKey)) {
            console.log(`Found new event: ${eventKey}, choice: ${choice}, wager: ${ethers.formatEther(wager)}`);
            
            // Check if this matches our current roll (within tolerance)
            const choiceMatch = Number(choice) === currentRollParams.current.choice;
            const wagerMatch = Math.abs(parseFloat(ethers.formatEther(wager)) - parseFloat(currentRollParams.current.wager)) < 0.0001;
            
            if (choiceMatch && wagerMatch) {
              console.log("Event matches our current roll! Showing result...");
              
              // Mark as processed
              processedEvents.current.add(eventKey);
              
              // Close animation popup
              setShowRollAnimation(false);
              
              // Show result popup
              showResultPopupNotification({
                won: won,
                choice: choice.toString(),
                resultNumber: result.toString(),
                wager: ethers.formatEther(wager),
                payout: ethers.formatEther(payout)
              });
              
              // Refresh all data
              await refreshAllData();
              
              // Stop polling
              clearInterval(pollInterval);
              isPolling.current = false;
              return;
            } else {
              console.log("Event doesn't match current roll parameters, skipping...");
            }
          }
        }
        
      } catch (error) {
        console.error(`Error in poll attempt ${attempts}:`, error);
      }
    }, 2000); // Poll every second
  };

  // Show result popup notification
  const showResultPopupNotification = (result) => {
    console.log("Showing result popup:", result);
    setRollResult(result);
    setShowResultPopup(true);
    
    // Auto-close after 15 seconds
    setTimeout(() => {
      if (showResultPopup) {
        setShowResultPopup(false);
      }
    }, 15000);
  };

  // Close animation popup (cancel roll)
  const closeAnimationPopup = () => {
    setShowRollAnimation(false);
    isPolling.current = false;
    setStatus({ 
      type: "info", 
      message: "Roll cancelled. The transaction was already submitted and will complete." 
    });
  };

  // Close result popup
  const closeResultPopup = () => {
    setShowResultPopup(false);
    setRollResult(null);
  };

  const rollAgain = () => {
    closeResultPopup();
    
    // Check if we have previous roll parameters
    if (previousRoll.choice && previousRoll.wager) {
      // Set the UI state to show the previous values
      setSelectedNumber(previousRoll.choice);
      setWager(previousRoll.wager);
      
      // Trigger roll with previous parameters
      rollDice(true);
    } else {
      // If no previous roll, just close the popup
      setStatus({ 
        type: "info", 
        message: "No previous roll found. Please select new numbers." 
      });
    }
  };

  // Deposit tokens
  const depositTokens = async () => {
    if (!checkNetworkBeforeAction()) return;
    
    if (!contract || !depositAmount || parseFloat(depositAmount) <= 0) {
      setStatus({ type: "error", message: "Please enter a valid deposit amount" });
      return;
    }
    
    try {
      setLoading(true);
      setStatus({ type: "pending", message: "Checking allowance..." });
      
      const depositWei = ethers.parseEther(depositAmount);
      const allowance = await token.allowance(account, CONTRACT_ADDRESS);
      
      if (allowance < depositWei) {
        const approved = await approveTokens(depositAmount);
        if (!approved) return;
      }
      
      setStatus({ type: "pending", message: "Depositing..." });
      const tx = await contract.deposit(depositWei);
      
      setStatus({ type: "pending", message: "Waiting for confirmation..." });
      await tx.wait();
      
      setStatus({ type: "success", message: "Deposit successful!" });
      setDepositAmount("");
      
      // Refresh data
      await Promise.all([
        loadContractData(contract, token),
        loadUserData(signer, contract, token)
      ]);
      
    } catch (error) {
      console.error("Error depositing:", error);
      setStatus({ type: "error", message: "Deposit failed: " + error.message });
    } finally {
      setLoading(false);
    }
  };

  // Withdraw tokens
  const withdrawTokens = async () => {
    if (!checkNetworkBeforeAction()) return;
    
    if (!contract || parseFloat(userWithdrawable) <= 0) {
      setStatus({ type: "error", message: "No funds to withdraw" });
      return;
    }
    
    try {
      setLoading(true);
      setStatus({ type: "pending", message: "Withdrawing..." });
      
      const tx = await contract.withdraw();
      
      setStatus({ type: "pending", message: "Waiting for confirmation..." });
      await tx.wait();
      
      setStatus({ type: "success", message: "Withdrawal successful!" });
      
      // Refresh data
      await Promise.all([
        loadContractData(contract, token),
        loadUserData(signer, contract, token)
      ]);
      
    } catch (error) {
      console.error("Error withdrawing:", error);
      setStatus({ type: "error", message: "Withdrawal failed: " + error.message });
    } finally {
      setLoading(false);
    }
  };

  // Load request history
  const loadRequestHistory = async () => {
    if (!contract || !account || !provider || (network && Number(network.chainId) !== 146)) {
      console.log("Skipping history load - missing dependencies");
      return;
    }
    
    try {
      // Get all RandomDiceRoll events for this user
      const filter = contract.filters.RandomDiceRoll(null, account);
      
      // Try to get events from recent blocks
      const currentBlock = await provider.getBlockNumber();
      const fromBlock = Math.max(currentBlock - 10000, 0); // Last 10k blocks
      
      const events = await contract.queryFilter(filter, fromBlock, currentBlock);
      
      console.log(`Found ${events.length} roll events total`);
      
      const requestsData = await Promise.all(
        events.map(async (event) => {
          try {
            const [requestId, requester, choice, result, wager, payout, won] = event.args;
            const block = await provider.getBlock(event.blockNumber);
            
            // Mark as processed for popup tracking
            const eventKey = `${requestId.toString()}-${event.transactionHash}`;
            processedEvents.current.add(eventKey);
            
            return {
              requestId: requestId.toString(),
              choice: choice.toString(),
              result: result.toString(),
              wager: ethers.formatEther(wager),
              payout: ethers.formatEther(payout),
              won: won,
              timestamp: block.timestamp,
              blockNumber: event.blockNumber,
              transactionHash: event.transactionHash
            };
          } catch (error) {
            console.error("Error processing event:", error);
            return null;
          }
        })
      );
      
      // Filter out null entries and sort by timestamp (newest first)
      const validRequests = requestsData
        .filter(req => req !== null)
        .sort((a, b) => b.timestamp - a.timestamp);
      
      setRequests(validRequests);
      
    } catch (error) {
      console.error("Error loading request history:", error);
    }
  };

  // Refresh all data
  const refreshAllData = async () => {
    if (!contract || !token || !signer) return;
    
    setLoading(true);
    try {
      await Promise.all([
        loadContractData(contract, token),
        loadUserData(signer, contract, token),
        loadRequestHistory()
      ]);
      setStatus({ type: "success", message: "Data refreshed!" });
    } catch (error) {
      console.error("Error refreshing data:", error);
      setStatus({ type: "error", message: "Refresh failed: " + error.message });
    } finally {
      setLoading(false);
    }
  };

  // Event listener for contract events
  useEffect(() => {
    if (!contract || !account || !provider || (network && Number(network.chainId) !== 146)) {
      return;
    }

    console.log("Setting up event listeners...");

    const onRollCompleted = async (requestId, requester, choice, result, wager, payout, won, event) => {
      const eventKey = `${requestId.toString()}-${event.transactionHash}`;
      
      // Only process if this is a NEW event
      if (requester.toLowerCase() === account.toLowerCase() && !processedEvents.current.has(eventKey)) {
        console.log("New roll event detected via listener:", eventKey);
        processedEvents.current.add(eventKey);
        
        // If we're showing animation, check if this matches current roll
        if (showRollAnimation) {
          const choiceMatch = Number(choice) === currentRollParams.current.choice;
          const wagerMatch = Math.abs(parseFloat(ethers.formatEther(wager)) - parseFloat(currentRollParams.current.wager)) < 0.0001;
          
          if (choiceMatch && wagerMatch) {
            console.log("Event matches current roll! Showing result...");
            setShowRollAnimation(false);
            showResultPopupNotification({
              won: won,
              choice: choice.toString(),
              resultNumber: result.toString(),
              wager: ethers.formatEther(wager),
              payout: ethers.formatEther(payout)
            });
            
            // Refresh data after a delay
            setTimeout(async () => {
              await refreshAllData();
            }, 2000);
          }
        }
      }
    };

    // Attach event listener
    contract.on("RandomDiceRoll", onRollCompleted);

    // Load initial history
    loadRequestHistory();

    // Cleanup function
    return () => {
      console.log("Cleaning up event listeners...");
      if (contract) {
        contract.off("RandomDiceRoll", onRollCompleted);
      }
    };
  }, [contract, account, provider, network, showRollAnimation]);

  // Auto-refresh data periodically (but not while showing animation)
  useEffect(() => {
    if (!contract || !account || !provider || (network && Number(network.chainId) !== 146) || showRollAnimation) {
      return;
    }

    console.log("Setting up auto-refresh...");
    
    const intervalId = setInterval(() => {
      console.log("Auto-refreshing data...");
      loadContractData(contract, token);
      loadUserData(signer, contract, token);
      // Only refresh history if not loading
      if (!loading) {
        loadRequestHistory();
      }
    }, 15000); // Refresh every 15 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [contract, account, signer, token, network, provider, loading, showRollAnimation]);

  // Render dice number buttons with dice images
  const renderDiceNumbers = () => {
    const numbers = [];
    for (let i = 1; i <= 20; i++) {
      numbers.push(
        <button
          key={i}
          className={`dice-btn ${selectedNumber === i ? 'selected' : ''}`}
          onClick={() => setSelectedNumber(i)}
          disabled={loading || isPaused || (network && Number(network.chainId) !== 146)}
        >
          <div className="dice-btn-content">
            <img 
              src={`${process.env.PUBLIC_URL}/dice/${i}.png`} 
              alt={`Dice ${i}`}
              className="dice-btn-image"
            />
            <span className="dice-btn-number">{i}</span>
          </div>
        </button>
      );
    }
    return numbers;
  };

  // Format number with commas
  const formatNumber = (num) => {
    return parseFloat(num).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    });
  };

  // Add a separate function for approval button disabled state
const isApproveButtonDisabled = () => {
  return loading || isPaused || (network && Number(network.chainId) !== 146) || isApprovingLarge || showRollAnimation;
};


  // Check if actions should be disabled
  const isActionDisabled = () => {
    return loading || isPaused || (network && Number(network.chainId) !== 146);
  };

  // Reset disclaimer acceptance (for testing/development)
  const resetDisclaimer = () => {
    localStorage.removeItem('derpquest_disclaimer_accepted');
    setHasAcceptedDisclaimer(false);
    setShowDisclaimer(true);
    setAccount("");
    setSigner(null);
    setContract(null);
    setToken(null);
  };

  return (
    <div className="container">
      {/* Disclaimer Modal */}
      {(showDisclaimer || !hasAcceptedDisclaimer) && (
        <DisclaimerModal onAccept={handleAcceptDisclaimer} />
      )}
      
      <header>
        <h1><img 
                    src={`${process.env.PUBLIC_URL}/dice/6.png`} 
                    alt={`Dice 6`}
                    className="dice-main-title-image"
                  /> DerpQuest Dice</h1>
        <p className="subtitle">Provably fair dice game powered by Paintswap VRF</p>
        <p className="network-badge">🌋 Sonic Mainnet Only (Chain ID: 146)</p>
        
       
      </header>
      
      

{account && network && Number(network.chainId) !== 146 && (
  <div className="network-warning">
    ⚠️ Wrong Network! Please switch to <strong>Sonic Mainnet (Chain ID: 146)</strong>
    <button 
      onClick={switchToSonicNetwork}
      style={{ marginLeft: '15px', padding: '5px 15px' }}
      disabled={loading}
    >
      {loading ? 'Switching...' : 'Switch Network'}
    </button>
  </div>
)}

      
      {/* Roll Animation Popup */}
      {showRollAnimation && <RollAnimationPopup onClose={closeAnimationPopup} />}
      
      {/* Result Popup */}
      <ResultPopup result={rollResult} onClose={closeResultPopup} rollAgain={rollAgain}/>
      
      {!account ? (
  <div className="connect-section">
    <h3>Sonic Mainnet Required</h3>
    <p style={{ marginBottom: '20px', color: '#aaa' }}>
      This dApp requires Sonic Mainnet (Chain ID: 146)
    </p>
    <p style={{ marginBottom: '20px', fontSize: '0.9rem', color: '#666' }}>
      Your wallet will automatically switch to Sonic Mainnet when you connect
    </p>
    <button 
      className="connect-btn"
      onClick={connectWallet}
      disabled={loading || !hasAcceptedDisclaimer}
    >
      {loading ? "Connecting..." : 
       !hasAcceptedDisclaimer ? "Please Accept Terms First" : 
       "Connect Wallet"}
    </button>
    
    {!hasAcceptedDisclaimer && (
      <button 
        className="disclaimer-review-btn"
        onClick={() => setShowDisclaimer(true)}
        style={{ marginTop: '15px', background: '#ff9800' }}
      >
        Review Terms and Conditions
      </button>
    )}
    
    <div style={{ marginTop: '20px', fontSize: '0.9rem', color: '#666' }}>
      <p><strong>Network Details:</strong></p>
      <p>Chain ID: 146</p>
      <p>RPC: https://rpc.soniclabs.com</p>
      <p>Explorer: https://sonicscan.org/</p>
      <p style={{ marginTop: '10px', color: '#ff9800' }}>
        ⚠️ Make sure you have Sonic Mainnet added to your wallet
      </p>
    </div>
  </div>
      ) : (
        <>
          <div className="wallet-info">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p>Connected: <strong>{account.substring(0, 6)}...{account.substring(account.length - 4)}</strong></p>
                <p>Your DERP Balance: <strong>{formatNumber(userBalance)} DERP</strong></p>
              </div>
              <button
                onClick={refreshAllData}
                disabled={loading || showRollAnimation}
                className="refresh-button"
              >
                {loading ? 'Refreshing...' : '🔄 Refresh'}
              </button>
            </div>
            
            {isPaused && (
              <p style={{ color: '#ff9800', marginTop: '10px' }}>
                ⚠️ Contract is currently paused
              </p>
            )}
            {network && Number(network.chainId) === 146 && (
              <p style={{ color: '#4caf50', marginTop: '5px' }}>
                ✓ Connected to Sonic Mainnet
              </p>
            )}
            {showRollAnimation && (
              <p style={{ color: '#ff9800', marginTop: '5px' }}>
                ⏳ Waiting for VRF randomness result...
              </p>
            )}
          </div>
          
          <div className="main-content">
            {/* Left Column: Dice Game */}
            <div className="card">
              <h2>🎲 Roll Dice</h2>
              
              <div className="info-box">
                <h4>How to Play:</h4>
                <p>1. Select a number (1-20)</p>
                <p>2. Enter your wager amount</p>
                <p>3. Roll the dice!</p>
                <p>🎯 Winners 19x their bet!</p>
                <p>⛽ VRF Gas Fee: ~{formatNumber(requestPrice)} S</p>
              </div>
              
              <div className="form-group">
                <label>Select Your Number (1-20):</label>
                <div className="dice-grid">
                  {renderDiceNumbers()}
                </div>
                {selectedNumber && (
                  <p style={{ textAlign: 'center', marginTop: '10px', color: '#00adb5' }}>
                    Selected: <strong>{selectedNumber}</strong>
                  </p>
                )}
              </div>
              
              <div className="form-group">
                <label>Wager Amount (DERP):</label>
                <input
                  type="number"
                  placeholder="Enter wager amount"
                  value={wager}
                  onChange={(e) => setWager(e.target.value)}
                  disabled={isActionDisabled() || showRollAnimation}
                  min="1"
                  step="1"
                />
                <p style={{ fontSize: '0.9rem', color: '#aaa', marginTop: '5px' }}>
                  Min: 1 DERP • Max: {formatNumber(maxWager)} DERP
                </p>
              </div>
              <button
                className="btn"
                onClick={approveLargeAmount}
                disabled={isApproveButtonDisabled()}
          
              >
                {isApprovingLarge ? "Approving..." : "APPROVE ALOT"}
              </button>
              <button
                className="btn"
                onClick={rollDice}
                disabled={!selectedNumber || !wager || isActionDisabled() || showRollAnimation}
              >
                {showRollAnimation ? "Rolling..." : (loading ? "Processing..." : `Roll Dice (Cost: ~${formatNumber(requestPrice)} S)`)}
              </button>
              
              <div className="balance-section">
                <div className="balance-card">
                  <h3>House Balance</h3>
                  <div className="balance-amount">{formatNumber(houseBalance)}</div>
                  <p>DERP</p>
                </div>
                <div className="balance-card">
                  <h3>Max Wager</h3>
                  <div className="balance-amount">{formatNumber(maxWager)}</div>
                  <p>DERP</p>
                </div>
              </div>
            </div>
            
           
          </div>
          
          {/* Status Messages */}
          {status.message && (
            <div className={`status ${status.type}`}>
              {status.type === 'pending' && (
                <div className="spinner" style={{ display: 'inline-block', marginRight: '10px' }}></div>
              )}
              {status.message}
            </div>
          )}
          
          {/* Request History */}
          {requests.length > 0 && (
            <div className="card" style={{ marginTop: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>📜 Your Roll History</h2>
                <button
                  onClick={loadRequestHistory}
                  disabled={loading || showRollAnimation}
                  className="refresh-button"
                  style={{ fontSize: '0.9rem', padding: '5px 10px' }}
                >
                  {loading ? 'Refreshing...' : '🔄'}
                </button>
              </div>
              <div className="request-history">
                {requests.map((req) => (
                  <div key={req.requestId} className={`request-item ${req.won ? 'won' : 'lost'}`}>
                    <div className="request-header">
                      <h4>Roll #{req.requestId.substring(0, 8)}...</h4>
                      <div className="request-dice">
                        <img 
                          src={`${process.env.PUBLIC_URL}/dice/${req.choice}.png`} 
                          alt={`Choice ${req.choice}`}
                          className="history-dice-image"
                        />
                        <span className="vs">VS</span>
                        <img 
                          src={`${process.env.PUBLIC_URL}/dice/${req.result}.png`} 
                          alt={`Result ${req.result}`}
                          className="history-dice-image"
                        />
                      </div>
                    </div>
                    <p>Choice: {req.choice} • Result: {req.result}</p>
                    <p>Wager: {req.wager} DERP • {req.won ? `Won: ${req.payout} DERP` : 'Lost'}</p>
                    <p style={{ fontSize: '0.8rem', color: '#aaa' }}>
                      {new Date(req.timestamp * 1000).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

           {/* Right Column: Deposit/Withdraw */}
           {isHouseAdmin && (
            <div className="card" style={{marginTop: "30px"}}>
              <h2>🏦 Decentralize the House</h2>
              
              <div className="info-box">
                <h4>Become a House Liquidity Provider:</h4>
                <p>• Deposit DERP to help grow the house</p>
                <p>• Earn a share of house profits</p>
                <p>• Withdraw anytime after 24 hours (5% fee on profits only)</p>
                <p>• Half of fee is burned, half stays in house</p>
                <p>WARNING: THE HOUSE CAN LOSE MONEY, ENTER AT YOUR OWN RISK</p>
              </div>
              
              <div className="form-group">
                <label>Deposit Amount (DERP):</label>
                <input
                  type="number"
                  placeholder="Enter deposit amount"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  disabled={isActionDisabled() || showRollAnimation}
                  min="1"
                  step="1"
                />
                <p style={{ fontSize: '0.9rem', color: '#aaa', marginTop: '5px' }}>
                  Minimum deposit: 1 DERP
                </p>
              </div>
              
              <button
                className="btn"
                onClick={depositTokens}
                disabled={!depositAmount || isActionDisabled() || showRollAnimation}
              >
                {loading ? "Processing..." : "Deposit DERP"}
              </button>
              
              <div className="balance-section" style={{ marginTop: '30px' }}>
                <div className="balance-card">
                  <h3>Your Deposit</h3>
                  <div className="balance-amount">{formatNumber(userDeposit)}</div>
                  <p>DERP</p>
                </div>
                <div className="balance-card">
                  <h3>Your Share</h3>
                  <div className="balance-amount">{formatNumber(userWithdrawable)}</div>
                  <p>DERP</p>
                </div>
              </div>
              
              <button
                className="btn btn-secondary"
                onClick={withdrawTokens}
                disabled={parseFloat(userWithdrawable) <= 0 || isActionDisabled() || showRollAnimation}
                style={{ marginTop: '20px' }}
              >
                {loading ? "Processing..." : "Withdraw All"}
              </button>
              
              <div className="stats-section" style={{ marginTop: '30px' }}>
                <h3>Game Stats</h3>
                <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '15px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00adb5' }}>{stats.total}</div>
                    <div>Total Rolls</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4caf50' }}>{stats.fulfilled}</div>
                    <div>Completed</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff9800' }}>{stats.pending}</div>
                    <div>Pending</div>
                  </div>
                </div>
              </div>
            </div>
            )}
        </>
      )}
    </div>
  );
}